/**
 * transcriptItemLinkerService — run the transcript item linker on a session,
 * list what it proposed, and record a person's decision on each proposal.
 *
 * Registered capability `transcript_item_linker` (Doc 22 §4; Settings → AI
 * Assists). The model only proposes: verifyProposals keeps a passage only when
 * its words are in the transcript, and record_transcript_links (migration 831)
 * writes the survivors as candidates. review_transcript_link is the only way a
 * candidate becomes a confirmed note or is rejected. Brief K slice 2b.
 */
import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import { isSupabaseAvailable } from './supabaseSync';
import { loadSessionTranscript } from './sessionTranscript';
import { useDeliberationStore } from '../stores/prioritization/deliberationStore';
import {
  LINKER_SYSTEM_PROMPT,
  buildLinkerPrompt,
  chunkTranscript,
  parseLinkerResponse,
  verifyProposals,
  type LinkerItem,
  type LinkerProposal,
} from './transcriptItemLinker';

export const TRANSCRIPT_LINKER_CAPABILITY = 'transcript_item_linker';

export interface TranscriptLinkerRun {
  ok: boolean;
  /** New passages waiting for a person. */
  proposed: number;
  /** Proposals dropped: words not in the transcript, an unknown item, or already on the item. */
  dropped: number;
  /** Why nothing ran, in words a person can act on. */
  note?: string;
  error?: string;
}

export interface TranscriptLinkCandidate {
  id: string;
  itemId: string;
  excerpt: string;
  transcriptTimestamp: string | null;
  createdAt: string;
}

export async function runTranscriptLinker(
  sessionId: string,
  workspaceId: string | null | undefined,
  items: LinkerItem[],
): Promise<TranscriptLinkerRun> {
  if (!isAiCapabilityEnabled(TRANSCRIPT_LINKER_CAPABILITY, workspaceId)) {
    return { ok: false, proposed: 0, dropped: 0, note: 'Finding passages is switched off for this workspace. An admin can turn on "Transcript item linker" in Settings → AI Assists.' };
  }
  if (!isSupabaseAvailable()) return { ok: false, proposed: 0, dropped: 0, error: 'The database is not available.' };
  if (items.length === 0) return { ok: false, proposed: 0, dropped: 0, note: 'This session has no ranked items to link.' };

  const transcript = await loadSessionTranscript(sessionId);
  if (!transcript.text.trim()) {
    return { ok: false, proposed: 0, dropped: 0, note: 'This session has no transcript yet. Paste one on the Documents tab, or record the session.' };
  }

  const proposals: LinkerProposal[] = [];
  let answered = 0;
  const chunks = chunkTranscript(transcript.text);
  for (const [i, chunk] of chunks.entries()) {
    const raw = await callFeatureLlm({
      userPrompt: buildLinkerPrompt(items, chunk.text),
      systemPrompt: LINKER_SYSTEM_PROMPT,
      conversationId: `transcript-item-linker-${sessionId}-${i}-${Date.now()}`,
      temperature: 0,
      maxTokens: 1500,
    });
    if (raw == null) continue;
    answered += 1;
    proposals.push(...parseLinkerResponse(raw, items.map((it) => it.id)));
  }
  if (answered === 0) return { ok: false, proposed: 0, dropped: 0, error: 'No AI provider answered. Try again in a moment.' };

  const verified = verifyProposals(transcript.text, proposals, items);
  // A note hangs off the session's deliberation record; create it the way every note does.
  if (workspaceId && !(await useDeliberationStore.getState().ensureRecord(sessionId, workspaceId))) {
    return { ok: false, proposed: 0, dropped: 0, error: 'The session record could not be opened. Try again.' };
  }
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('record_transcript_links', {
    p_session_id: sessionId,
    p_links: verified.map((l) => ({ item_id: l.itemId, excerpt: l.excerpt, transcript_timestamp: l.transcriptTimestamp })),
  });
  const result = (data ?? {}) as { success?: boolean; error?: string; proposed?: number; skipped?: number };
  if (error || result.success !== true) {
    return { ok: false, proposed: 0, dropped: 0, error: result.error ?? error?.message ?? 'The passages could not be saved.' };
  }
  const proposed = result.proposed ?? 0;
  return { ok: true, proposed, dropped: proposals.length - proposed };
}

/** Passages the linker proposed for this session that nobody has decided on yet. */
export async function fetchTranscriptLinkCandidates(sessionId: string): Promise<TranscriptLinkCandidate[]> {
  if (!isSupabaseAvailable()) return [];
  const { data } = await getTypedSupabaseClient()
    .from('deliberation_item_annotations')
    .select('id, item_id, content, transcript_timestamp, created_at')
    .eq('session_id', sessionId)
    .eq('status', 'candidate')
    .is('retracted_at', null)
    .order('created_at');
  return (data ?? []).map((r) => ({
    id: r.id,
    itemId: r.item_id,
    excerpt: r.content,
    transcriptTimestamp: r.transcript_timestamp,
    createdAt: r.created_at,
  }));
}

/** A person's decision on one proposed passage. Confirmed passages become notes on the item. */
export async function reviewTranscriptLink(
  annotationId: string,
  decision: 'confirmed' | 'rejected',
): Promise<{ ok: boolean; error?: string }> {
  if (!isSupabaseAvailable()) return { ok: false, error: 'The database is not available.' };
  const { data, error } = await getTypedSupabaseClient().rpc('review_transcript_link', {
    p_annotation_id: annotationId,
    p_decision: decision,
  });
  const result = (data ?? {}) as { success?: boolean; error?: string };
  if (error || result.success !== true) return { ok: false, error: result.error ?? error?.message ?? 'That decision could not be saved.' };
  return { ok: true };
}
